import React from 'react';

import { ScreenCardInView } from 'containers/Intelligence/filters/screenFilterSignature';
import useMultiLanguageTranslation from 'hooks/useMultiLanguageTranslation';

import {
  ORBIT_CARD_CHIP_LIMIT,
  describeOrbitCardChip,
  describeOrbitMoreCards
} from '../contextLabel';
import OrbitGlyphText from '../glyphIcons';
import { OrbitChip } from '../styles';
import OrbitChipIcon from './OrbitChipIcon';

export interface OrbitCardChipsProps {
  /** Every card the reader has on screen, in reading order. */
  cards: ScreenCardInView[];
  /** The row these chips are drawn in, as the stem of each chip's `data-testid`. */
  testIdPrefix: string;
}

/**
 * One chip per card the reader has on screen, in reading order, capped at `ORBIT_CARD_CHIP_LIMIT`
 * with whatever is past the cap collapsed into a "+N more" chip. Shared by the panel's context block
 * and the conflict card so the two rows cannot read differently. PLA-8844.
 *
 * PERX3-REDESIGN round 7: every chip label goes through `OrbitGlyphText` - the copy opens on the
 * frame's own emoji, which under the flag is drawn as the matching antd icon and with it off is
 * the string verbatim. Both callers relied on that, so the shared component has to carry it.
 */
const OrbitCardChips: React.FC<OrbitCardChipsProps> = ({ cards, testIdPrefix }) => {
  const { t } = useMultiLanguageTranslation();
  const hidden = cards.length - ORBIT_CARD_CHIP_LIMIT;

  return (
    <>
      {cards.slice(0, ORBIT_CARD_CHIP_LIMIT).map(card => (
        <OrbitChip key={card.key} data-testid={`${testIdPrefix}-chip-card-${card.key}`}>
          <OrbitChipIcon kind="card" />
          <OrbitGlyphText text={describeOrbitCardChip(t, card)} />
        </OrbitChip>
      ))}
      {hidden > 0 ? (
        <OrbitChip data-testid={`${testIdPrefix}-chip-card-more`}>
          <OrbitChipIcon kind="card" />
          <OrbitGlyphText text={describeOrbitMoreCards(t, hidden)} />
        </OrbitChip>
      ) : null}
    </>
  );
};

export default OrbitCardChips;
